import { useEffect, useState } from "react";
import { Plus, Trash2, Newspaper, Instagram, Facebook, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/ImageUpload";
import api, { formatApiErrorDetail, getFileUrl } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { LANDING_ADMIN } from "@/constants/testIds";

const EMPTY_FORM = { image_url: null, platform: "instagram", link_url: "", caption: "" };
const PLATFORM_LABELS = { instagram: "Instagram", facebook: "Facebook" };
const PLATFORM_ICONS = { instagram: Instagram, facebook: Facebook };

/** Fix BD (2026-10-30): card de Configuración para las "Novedades" de la página principal: publicaciones de
 * Instagram/Facebook cargadas a mano (foto, red, link a la publicación y texto). La página muestra las 6
 * activas más recientes. */
export default function NewsPostsSettingsCard() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { toast } = useToast();

  const load = () => api.get("/landing/posts").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.image_url) {
      toast({ title: "Subí la foto de la publicación", variant: "destructive" });
      return;
    }
    const link = form.link_url.trim();
    if (link && !/^https?:\/\/\S+$/.test(link)) {
      toast({ title: "El link tiene que empezar con https://", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await api.post("/landing/posts", {
        image_url: form.image_url,
        platform: form.platform,
        link_url: link || null,
        caption: form.caption.trim() || null,
      });
      setForm(EMPTY_FORM);
      setAdding(false);
      load();
      toast({ title: "Publicación agregada" });
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/landing/posts/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/landing/posts/${deleteTarget.id}`);
      load();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5" data-testid={LANDING_ADMIN.newsCard}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-foreground">Novedades de redes</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Publicaciones de Instagram o Facebook que se muestran en la página principal, en la sección &quot;Novedades&quot;. Se ven las 6 activas más recientes.
          </p>
        </div>
        {!adding && (
          <Button size="sm" variant="outline" className="gap-2 shrink-0" onClick={() => setAdding(true)} data-testid={LANDING_ADMIN.newsAddButton}>
            <Plus className="w-4 h-4" /> Nueva
          </Button>
        )}
      </div>

      {adding && (
        <div className="border border-border rounded-lg p-4 space-y-3">
          <ImageUpload currentUrl={form.image_url} onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} placeholderUrl={null} testId={LANDING_ADMIN.newsImageUpload} shape="square" />
          <Select value={form.platform} onValueChange={(v) => setForm((f) => ({ ...f, platform: v }))}>
            <SelectTrigger className="w-48" data-testid={LANDING_ADMIN.newsPlatformSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Link a la publicación (opcional), ej. https://www.instagram.com/p/..." value={form.link_url} onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))} data-testid={LANDING_ADMIN.newsLinkInput} />
          <Textarea placeholder="Texto (opcional)" rows={3} maxLength={500} value={form.caption} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))} data-testid={LANDING_ADMIN.newsCaptionInput} />
          <div className="flex gap-2">
            <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={handleAdd} disabled={saving} data-testid={LANDING_ADMIN.newsSaveButton}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setForm(EMPTY_FORM); }} data-testid={LANDING_ADMIN.newsCancelButton}>Cancelar</Button>
          </div>
        </div>
      )}

      {items.length === 0 && !adding && (
        <div className="text-center py-6">
          <Newspaper className="w-6 h-6 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavía no hay publicaciones cargadas.</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((i) => {
          const Icon = PLATFORM_ICONS[i.platform] || Instagram;
          return (
            <div key={i.id} className="flex items-start justify-between gap-3 border border-border rounded-lg p-3" data-testid={`${LANDING_ADMIN.newsRowPrefix}${i.id}`}>
              <div className="min-w-0 flex items-start gap-3">
                <img src={getFileUrl(i.image_url)} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-brand" /> {PLATFORM_LABELS[i.platform] || i.platform}
                    {i.link_url && (
                      <a href={i.link_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-brand" aria-label="Abrir la publicación">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </p>
                  {i.caption && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{i.caption}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={i.is_active} onCheckedChange={() => toggleActive(i)} data-testid={`${LANDING_ADMIN.newsActiveTogglePrefix}${i.id}`} />
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(i)} data-testid={`${LANDING_ADMIN.newsDeleteButtonPrefix}${i.id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta publicación?</AlertDialogTitle>
            <AlertDialogDescription>Deja de verse en la página. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid={LANDING_ADMIN.newsDeleteConfirmButton}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
